import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  CreateQualityApprovalMutation,
  QualityApprovalCreateFailure,
  QualityApprovalCreateInput,
} from "@/graphql/codegen/graphql"
import { CreateQualityApprovalDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseCreateQualityApprovalReturn {
  createQualityApproval: (input: QualityApprovalCreateInput) => Promise<boolean>
  isCreating: boolean
}

export const useCreateQualityApproval = (): UseCreateQualityApprovalReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    CreateQualityApprovalMutation,
    { input: QualityApprovalCreateInput }
  >(CreateQualityApprovalDocument)

  const createQualityApproval = useCallback(
    async (input: QualityApprovalCreateInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.qualityApproval.create
      const hasError = error || output?.__typename === "QualityApprovalCreateFailure"

      if (hasError) {
        const message = error?.message ?? (output as QualityApprovalCreateFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      notify({ type: NotificationType.Success, message: "Out of spec approval requested" })

      return true
    },
    [executeMutation, notify]
  )

  return { createQualityApproval, isCreating: fetching }
}
